import type { DraftGenerationSummary as GenerationSummary } from "../../api/queries/adminQueries";
import { DraftPromptInspector } from "./DraftPromptInspector";

type DraftGenerationSummaryProps = {
  generation: GenerationSummary;
  formatDate: (iso: string) => string;
};

const formatRole = (role: string) =>
  role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const shortHash = (hash: string | null | undefined) =>
  hash ? hash.slice(0, 8) : null;

export const DraftGenerationSummaryPanel = ({
  generation,
  formatDate,
}: DraftGenerationSummaryProps) => {
  const draftAttempts = generation.attempts.filter(
    (a) =>
      a.prompt_role === "email_draft" || a.prompt_role === "email_correction",
  );
  const primaryDraft =
    draftAttempts.find((a) => a.attempt_kind === "initial") ?? draftAttempts[0];
  const guardrailAttempts = generation.attempts.filter(
    (a) => a.prompt_role === "guardrail_validation",
  );

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-4">
      <h3 className="text-sm font-semibold text-slate-700">Generation</h3>
      <div className="mt-2 grid gap-2 text-sm text-slate-600 sm:grid-cols-2 lg:grid-cols-3">
        <p>
          <span className="font-medium text-slate-700">Trace status:</span>{" "}
          {generation.status}
        </p>
        <p>
          <span className="font-medium text-slate-700">Language:</span>{" "}
          {generation.language || "—"}
        </p>
        <p>
          <span className="font-medium text-slate-700">Traced at:</span>{" "}
          {formatDate(generation.created_at)}
        </p>
        <p className="sm:col-span-2 lg:col-span-3">
          <span className="font-medium text-slate-700">Trace ID:</span>{" "}
          <code className="rounded bg-white px-1 py-0.5 text-xs">
            {generation.trace_id}
          </code>
        </p>
      </div>

      {primaryDraft ? (
        <div className="mt-4 rounded-md border border-slate-200 bg-white p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Primary draft attempt
          </p>
          <dl className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium text-slate-700">Model</dt>
              <dd className="text-slate-600">
                {primaryDraft.model_provider && primaryDraft.model_name
                  ? `${primaryDraft.model_provider} / ${primaryDraft.model_name}`
                  : "—"}
                {primaryDraft.escalated ? " (escalated)" : ""}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-slate-700">Prompt</dt>
              <dd className="text-slate-600">
                {primaryDraft.prompt_template_key
                  ? `${primaryDraft.prompt_template_key}${shortHash(primaryDraft.prompt_content_hash) ? ` (${shortHash(primaryDraft.prompt_content_hash)})` : ""}`
                  : "—"}
              </dd>
            </div>
            {primaryDraft.latency_seconds != null ? (
              <div>
                <dt className="font-medium text-slate-700">Latency</dt>
                <dd className="text-slate-600">
                  {(primaryDraft.latency_seconds * 1000).toFixed(0)} ms
                </dd>
              </div>
            ) : null}
          </dl>
        </div>
      ) : null}

      {generation.attempts.length > 0 ? (
        <div className="mt-4 overflow-x-auto">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            All attempts ({generation.attempts.length})
          </p>
          <table className="min-w-full divide-y divide-slate-200 text-xs">
            <thead className="bg-white">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium text-slate-600">
                  #
                </th>
                <th className="px-2 py-1.5 text-left font-medium text-slate-600">
                  Role
                </th>
                <th className="px-2 py-1.5 text-left font-medium text-slate-600">
                  Kind
                </th>
                <th className="px-2 py-1.5 text-left font-medium text-slate-600">
                  Model
                </th>
                <th className="px-2 py-1.5 text-left font-medium text-slate-600">
                  Prompt
                </th>
                <th className="px-2 py-1.5 text-left font-medium text-slate-600">
                  Guardrail
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white/60">
              {generation.attempts.map((attempt) => (
                <tr key={attempt.id}>
                  <td className="px-2 py-1.5 text-slate-600">
                    {attempt.attempt_index}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600">
                    {formatRole(attempt.prompt_role)}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600">
                    {formatRole(attempt.attempt_kind)}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600">
                    {attempt.model_name
                      ? `${attempt.model_provider ?? ""}/${attempt.model_name}`.replace(
                          /^\//,
                          "",
                        )
                      : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600">
                    {attempt.prompt_template_key
                      ? `${attempt.prompt_template_key}${shortHash(attempt.prompt_content_hash) ? ` (${shortHash(attempt.prompt_content_hash)})` : ""}`
                      : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600">
                    {attempt.guardrail_verdict
                      ? `${attempt.guardrail_verdict}${
                          attempt.guardrail_detail
                            ? `: ${attempt.guardrail_detail}`
                            : ""
                        }`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {guardrailAttempts.length > 0 && !primaryDraft ? (
        <p className="mt-2 text-sm text-slate-500">
          {guardrailAttempts.length} guardrail attempt(s) recorded; no email
          draft attempt in trace.
        </p>
      ) : null}

      <DraftPromptInspector generation={generation} />
    </div>
  );
};
